/**
 * useReportListSignalR Hook
 *
 * Custom React hook for connecting to the CSReportingWebService SignalR hub
 * and receiving real-time notifications for ReportList and ReportListItem changes.
 *
 * @example
 * ```tsx
 * function ReportListPage() {
 *   const { isConnected, connectionState } = useReportListSignalR({
 *     eventHandlers: {
 *       ReportListCreated: (data) => {
 *         console.log("New report list created:", data.reportListKey)
 *         refetchReportLists()
 *       },
 *       ReportListItemCreated: (data) => {
 *         console.log("New item added to:", data.reportListKey)
 *         refetchItems(data.reportListKey)
 *       }
 *     }
 *   })
 *
 *   return (
 *     <div>
 *       <ConnectionStatus isConnected={isConnected} />
 *       <ReportListTable />
 *     </div>
 *   )
 * }
 * ```
 */

import { useEffect, useRef, useState, useCallback, type RefObject } from "react"
import * as signalR from "@microsoft/signalr"
import { useAuth as useOidcAuth } from "react-oidc-context"
import { AUTH_DISABLED } from "@/authConfig"
import { setSignalRConnectionId, getSignalRConnectionId } from "@/lib/signalr-connection-id"
import type {
  UseReportListSignalROptions,
  UseReportListSignalRReturn,
  SignalRConnectionState,
  SignalREventHandlers,
} from "@/types/signalr.types"
import { SignalRConnectionState as ConnectionState } from "@/types/signalr.types"

// ============================================================================
// Configuration
// ============================================================================

/**
 * Get the SignalR hub URL from environment variables
 */
const getHubUrl = (): string => {
  const baseUrl =
    import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || ""
  const apiPath = import.meta.env.VITE_API_PATH || ""

  if (!baseUrl) {
    console.warn(
      "⚠️ API base URL not configured. Set VITE_API_BASE_URL or VITE_API_URL",
    )
    return "/hubs/reportList" // Fallback to relative URL
  }

  return `${baseUrl.replace(/\/$/, "")}${apiPath}/hubs/reportList`
}

/**
 * Exponential backoff retry policy: 0s, 2s, 10s, 30s, then 30s forever
 */
const retryPolicy: signalR.IRetryPolicy = {
  nextRetryDelayInMilliseconds(retryContext) {
    if (retryContext.previousRetryCount === 0) return 0
    if (retryContext.previousRetryCount === 1) return 2000
    if (retryContext.previousRetryCount === 2) return 10000
    return 30000
  },
}

/**
 * Retrieve auth token for SignalR connection.
 * Extracted to reduce cognitive complexity of connect().
 */
function createAccessTokenFactory(
  auth: { isAuthenticated: boolean; user?: { access_token?: string } | null },
  enableLogging: boolean,
): () => Promise<string> {
  return async () => {
    if (AUTH_DISABLED) {
      if (enableLogging) console.log("SignalR: Auth disabled, no token")
      return ""
    }

    if (!auth.isAuthenticated || !auth.user) {
      if (enableLogging) {
        console.error("SignalR: User not authenticated", {
          isAuthenticated: auth.isAuthenticated,
          hasUser: !!auth.user,
        })
      }
      throw new Error("User is not authenticated")
    }

    const token = auth.user.access_token || ""
    if (enableLogging) {
      console.log("SignalR: Got auth token", {
        hasToken: !!token,
        tokenLength: token.length,
        tokenPreview: token.substring(0, 20) + "...",
      })
    }

    return token
  }
}

/**
 * Create named event handler wrappers that delegate to the ref-based handlers.
 * Extracted to reduce cognitive complexity of connect().
 */
function createEventHandlers(
  connection: signalR.HubConnection,
  eventHandlersRef: RefObject<SignalREventHandlers>,
  enableLogging: boolean,
): Record<string, (data: any) => void> {
  const logAndDispatch = (eventName: string, emoji: string) => (data: unknown) => {
    if (enableLogging) console.log(`${emoji} ${eventName}:`, data)
    eventHandlersRef.current?.[eventName as keyof SignalREventHandlers]?.(data as any)
  }

  return {
    ReportListCreated: logAndDispatch("ReportListCreated", "📝"),
    ReportListUpdated: logAndDispatch("ReportListUpdated", "📝"),
    ReportListDeleted: logAndDispatch("ReportListDeleted", "🗑️"),
    ReportListItemCreated: logAndDispatch("ReportListItemCreated", "➕"),
    ReportListItemUpdated: (data: unknown) => {
      if (enableLogging) {
        console.log("📝 ReportListItemUpdated:", data)
        console.log("🔗 Live connection ID:", connection.connectionId)
        console.log("🔗 Stored connection ID:", getSignalRConnectionId())
      }
      eventHandlersRef.current?.ReportListItemUpdated?.(data as any)
    },
    ReportListItemDeleted: logAndDispatch("ReportListItemDeleted", "➖"),
    MultipleReportListItemsCreated: (data: any) => {
      if (enableLogging)
        console.log(`➕×${data.count} MultipleReportListItemsCreated:`, data)
      eventHandlersRef.current?.MultipleReportListItemsCreated?.(data)
    },
    MultipleReportListItemsDeleted: (data: any) => {
      if (enableLogging)
        console.log(`➖×${data.count} MultipleReportListItemsDeleted:`, data)
      eventHandlersRef.current?.MultipleReportListItemsDeleted?.(data)
    },
  }
}

/**
 * Remove all handlers from a connection and unregister them.
 */
function removeHandlers(
  registeredHandlersRef: RefObject<{
    connection: signalR.HubConnection
    handlers: Record<string, (...args: unknown[]) => void>
  } | null>,
): void {
  if (!registeredHandlersRef.current) return
  const { connection, handlers } = registeredHandlersRef.current
  for (const [event, handler] of Object.entries(handlers)) {
    connection.off(event, handler)
  }
  registeredHandlersRef.current = null
}

/**
 * Handle SignalR connection close events.
 * Extracted to reduce cognitive complexity of connect().
 */
function handleConnectionClose(
  closeError: Error | undefined,
  setConnectionState: (state: SignalRConnectionState) => void,
  setError: (err: Error | null) => void,
  enableLogging: boolean,
): void {
  setConnectionState(ConnectionState.Disconnected)
  setSignalRConnectionId(null)
  if (!closeError) {
    if (enableLogging) console.warn("⚠️ SignalR connection closed gracefully by server")
    return
  }
  const errorMessage = closeError instanceof Error ? closeError.message : String(closeError)
  setError(closeError instanceof Error ? closeError : new Error(errorMessage))
  if (enableLogging) {
    console.error("✗ SignalR connection closed with error:", closeError)
    console.error("Error details:", errorMessage)
  }
}

/**
 * Stop any existing connection and clean up handlers.
 * Extracted to reduce cognitive complexity of connect().
 */
function stopExistingConnection(
  connectionRef: RefObject<signalR.HubConnection | null>,
  registeredHandlersRef: RefObject<{
    connection: signalR.HubConnection
    handlers: Record<string, (...args: unknown[]) => void>
  } | null>,
  enableLogging: boolean,
): void {
  if (!connectionRef.current) return
  if (enableLogging) console.log("SignalR: stopping previous connection before reconnect")
  removeHandlers(registeredHandlersRef)
  // Fire-and-forget stop — errors are intentionally swallowed
  connectionRef.current.stop().catch(() => { /* ignore */ })
  connectionRef.current = null
  setSignalRConnectionId(null)
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useReportListSignalR(
  options: UseReportListSignalROptions = {},
): UseReportListSignalRReturn {
  const {
    eventHandlers = {},
    autoConnect = true,
    enableLogging = import.meta.env.DEV,
    hubUrl,
  } = options

  // Get OIDC auth context
  const auth = useOidcAuth()

  // State
  const [connectionState, setConnectionState] =
    useState<SignalRConnectionState>(ConnectionState.Disconnected)
  const [error, setError] = useState<Error | null>(null)

  // Refs
  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const eventHandlersRef = useRef<SignalREventHandlers>(eventHandlers)
  const registeredHandlersRef = useRef<{
    connection: signalR.HubConnection
    handlers: Record<string, (...args: unknown[]) => void>
  } | null>(null)

  // Update event handlers ref when they change
  useEffect(() => {
    eventHandlersRef.current = eventHandlers
  }, [eventHandlers])

  // ============================================================================
  // Connection Management
  // ============================================================================

  const connect = useCallback(async () => {
    if (enableLogging) console.log("SignalR connect() called")

    // Always stop any existing connection first (handles StrictMode remount)
    stopExistingConnection(connectionRef, registeredHandlersRef, enableLogging)

    try {
      setConnectionState(ConnectionState.Connecting)
      setError(null)

      const url = hubUrl || getHubUrl()
      if (enableLogging) console.log("SignalR connecting to:", url)

      // Build SignalR connection
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: createAccessTokenFactory(auth, enableLogging),
        })
        .withAutomaticReconnect(retryPolicy)
        .configureLogging(
          enableLogging ? signalR.LogLevel.Information : signalR.LogLevel.Warning,
        )
        .build()

      // Store in ref BEFORE start() so cleanup can find it during StrictMode remount
      connectionRef.current = connection

      // Connection state handlers
      connection.onreconnecting((reconnectError) => {
        setConnectionState(ConnectionState.Reconnecting)
        if (enableLogging) console.warn("⟳ SignalR reconnecting...", reconnectError)
      })

      connection.onreconnected((connectionId) => {
        setConnectionState(ConnectionState.Connected)
        setError(null)
        setSignalRConnectionId(connectionId || null)
        if (enableLogging) console.log("✓ SignalR reconnected:", connectionId)
      })

      connection.onclose((closeError) =>
        handleConnectionClose(closeError, setConnectionState, setError, enableLogging),
      )

      // Register event handlers
      const handlers = createEventHandlers(connection, eventHandlersRef, enableLogging)
      for (const [event, handler] of Object.entries(handlers)) {
        connection.on(event, handler)
      }
      registeredHandlersRef.current = { connection, handlers }

      // Start connection
      await connection.start()

      // Verify we're still the current connection (not replaced by StrictMode remount)
      if (connectionRef.current !== connection) {
        if (enableLogging) console.log("SignalR: connection superseded during start, stopping")
        for (const [event, handler] of Object.entries(handlers)) {
          connection.off(event, handler)
        }
        await connection.stop()
        return
      }

      setConnectionState(ConnectionState.Connected)
      setSignalRConnectionId(connection.connectionId || null)

      if (enableLogging) {
        console.log("✓ SignalR connected to:", url)
        console.log("🔗 Connection ID:", connection.connectionId)
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error(String(err))
      setError(errorObj)
      setConnectionState(ConnectionState.Disconnected)
      console.error("✗ SignalR connection failed:", errorObj)
    }
  }, [hubUrl, enableLogging, auth.isAuthenticated, auth.user])

  const disconnect = useCallback(async () => {
    // Remove all registered event handlers first
    removeHandlers(registeredHandlersRef)
    if (enableLogging) console.log("SignalR: removed event handlers")

    if (connectionRef.current) {
      try {
        await connectionRef.current.stop()
        connectionRef.current = null
        setConnectionState(ConnectionState.Disconnected)
        setSignalRConnectionId(null)
        if (enableLogging) console.log("SignalR disconnected")
      } catch (err) {
        console.error("Error disconnecting SignalR:", err)
      }
    }
  }, [enableLogging])

  const reconnect = useCallback(async () => {
    await disconnect()
    await connect()
  }, [connect, disconnect])

  // ============================================================================
  // Lifecycle
  // ============================================================================

  useEffect(() => {
    // Only connect if autoConnect is true and user is authenticated (or auth is disabled)
    const shouldConnect = autoConnect && (AUTH_DISABLED || auth.isAuthenticated)
    
    if (enableLogging) {
      console.log("SignalR connection check:", {
        autoConnect,
        AUTH_DISABLED,
        isAuthenticated: auth.isAuthenticated,
        hasUser: !!auth.user,
        hasAccessToken: !!auth.user?.access_token,
        shouldConnect,
        currentState: connectionState,
      })
    }
    
    if (shouldConnect) {
      connect()
    } else {
      // Only disconnect when we explicitly should NOT be connected
      disconnect()
    }

    // No cleanup disconnect here — connect() already stops previous connections
    // before starting a new one. Disconnecting in cleanup races with the async
    // connect(), aborting the handshake when the effect re-runs due to token refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect, auth.isAuthenticated, auth.user?.access_token])

  // ============================================================================
  // Return Values
  // ============================================================================

  return {
    connectionState,
    isConnected: connectionState === ConnectionState.Connected,
    isConnecting:
      connectionState === ConnectionState.Connecting ||
      connectionState === ConnectionState.Reconnecting,
    error,
    reconnect,
    disconnect,
  }
}
